import React, { useState, useEffect, useCallback, useRef } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { Loader2, AlertCircle, X, Plus, Trash2, Info } from "lucide-react";

const API_BASE_URL = "https://app01.tmsdev.space/api/v2/dmonitor";
const SOURCES = ["news", "twitter", "igr", "facebook"];

const getColorForQuery = (query) => {
  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-yellow-500",
    "bg-red-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-teal-500",
  ];
  const index =
    query.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0) %
    colors.length;
  return colors[index];
};

// Refined Indonesian negative sentiment lexicon
const negativeLexicon = {
  // Strongly negative words
  buruk: -3,
  jelek: -3,
  bodoh: -3,
  payah: -3,
  gagal: -3,
  hancur: -3,
  benci: -4,
  marah: -3,
  kecewa: -3,
  menyebalkan: -4,
  mengerikan: -4,
  jahat: -4,
  busuk: -4,
  korupsi: -4,
  skandal: -3,
  krisis: -3,
  memalukan: -3,
  menjijikkan: -4,
  berbahaya: -3,
  menghancurkan: -4,
  kejam: -4,
  menyedihkan: -3,
  mengerikan: -4,
  brutal: -4,

  // Moderately negative words
  lemah: -2,
  kurang: -2,
  sulit: -2,
  masalah: -2,
  ketidakadilan: -2,
  "tidak kompeten": -3,
  "tidak mampu": -3,
  salah: -2,
  keliru: -2,
  menolak: -2,
  melanggar: -3,
  ketidakpuasan: -2,
  mengecewakan: -3,

  // Political negative terms
  koruptor: -4,
  penipuan: -4,
  manipulasi: -3,
  otoriter: -3,
  nepotisme: -3,
  kolusi: -3,
  diktator: -4,
  tirani: -4,
  ketidakpercayaan: -3,
  ketidakjujuran: -3,
  ketidakadilan: -3,

  // Intensifiers (when used with negative words)
  sangat: 1.5,
  sekali: 1.5,
  terlalu: 1.2,
  sungguh: 1.3,

  // Negations (can flip sentiment)
  tidak: -1,
  bukan: -1,
  jangan: -1,
  belum: -0.5,
  tanpa: -1,
};

const SentimentDashboard = () => {
  const [query, setQuery] = useState("");
  const [activeSearches, setActiveSearches] = useState(["prabowo"]);
  const [archivedSearches, setArchivedSearches] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newItems, setNewItems] = useState({});
  const [showInfo, setShowInfo] = useState(false);
  const infoRef = useRef(null);

  const analyzeNegativeSentiment = useCallback((text) => {
    const words = text.toLowerCase().split(/\s+/);
    let score = 0;
    let negativeCount = 0;
    let intensifier = 1;
    let negation = 1;

    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const prevWord = i > 0 ? words[i - 1] : "";
      const nextWord = i < words.length - 1 ? words[i + 1] : "";

      if (negativeLexicon.hasOwnProperty(word)) {
        let wordScore = negativeLexicon[word];

        // Apply intensifiers
        if (["sangat", "sekali", "terlalu", "sungguh"].includes(prevWord)) {
          wordScore *= intensifier;
        }

        // Apply negation
        wordScore *= negation;

        score += wordScore;
        if (wordScore < 0) negativeCount++;

        // Reset modifiers
        intensifier = 1;
        negation = 1;
      } else if (
        ["tidak", "bukan", "jangan", "belum", "tanpa"].includes(word)
      ) {
        negation = -1;
      } else if (["sangat", "sekali", "terlalu", "sungguh"].includes(word)) {
        intensifier = negativeLexicon[word];
      }

      // Check for negative phrases
      const bigram = `${word} ${nextWord}`.trim();
      if (negativeLexicon.hasOwnProperty(bigram)) {
        score += negativeLexicon[bigram];
        negativeCount++;
        i++; // Skip the next word as it's part of the bigram
      }
    }

    // Normalize score based on text length and negative word count
    const normalizedScore = score / Math.sqrt(words.length);

    // Classify as negative if normalized score is below threshold and there are enough negative words
    return normalizedScore < -1.5 && negativeCount >= 2;
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const newResults = {};
    try {
      for (const searchQuery of activeSearches) {
        newResults[searchQuery] = {};
        for (const source of SOURCES) {
          const response = await fetch(
            `${API_BASE_URL}/${source}?q=${searchQuery}`
          );
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          const items =
            data[source] || data[source.replace("igr", "instagram")] || [];
          newResults[searchQuery][source] = items.filter((item) =>
            analyzeNegativeSentiment(item.description || item.title || "")
          );
        }
      }
      setResults((prevResults) => {
        const newItems = {};
        activeSearches.forEach((searchQuery) => {
          newItems[searchQuery] = {};
          SOURCES.forEach((source) => {
            newItems[searchQuery][source] = newResults[searchQuery][
              source
            ].filter(
              (item) =>
                !prevResults[searchQuery]?.[source]?.some(
                  (prevItem) => prevItem.url === item.url
                )
            );
          });
        });
        setNewItems(newItems);
        return newResults;
      });
    } catch (e) {
      setError("An error occurred while fetching data. Please try again.");
      console.error("Fetch error:", e);
    } finally {
      setLoading(false);
    }
  }, [activeSearches, analyzeNegativeSentiment]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (
      query &&
      !activeSearches.includes(query) &&
      !archivedSearches.includes(query)
    ) {
      setActiveSearches((prev) => [query, ...prev]);
      setQuery("");
    }
  };

  const removeSearch = (searchToRemove) => {
    setActiveSearches((prev) => prev.filter((s) => s !== searchToRemove));
    setArchivedSearches((prev) => [searchToRemove, ...prev]);
    setResults((prev) => {
      const newResults = { ...prev };
      delete newResults[searchToRemove];
      return newResults;
    });
    setNewItems((prev) => {
      const newNewItems = { ...prev };
      delete newNewItems[searchToRemove];
      return newNewItems;
    });
  };
  const recallSearch = (searchToRecall) => {
    setArchivedSearches((prev) => prev.filter((s) => s !== searchToRecall));
    setActiveSearches((prev) => [searchToRecall, ...prev]);
  };

  const deleteArchivedSearch = (searchToDelete) => {
    setArchivedSearches((prev) => prev.filter((s) => s !== searchToDelete));
    setResults((prev) => {
      const newResults = { ...prev };
      delete newResults[searchToDelete];
      return newResults;
    });
    setNewItems((prev) => {
      const newNewItems = { ...prev };
      delete newNewItems[searchToDelete];
      return newNewItems;
    });
  };

  const truncateText = (text, lines) => {
    const words = text.split(" ");
    const truncated = words.slice(0, lines * 10).join(" ");
    return truncated.length < text.length ? `${truncated}...` : truncated;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (infoRef.current && !infoRef.current.contains(event.target)) {
        setShowInfo(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleInfo = () => {
    setShowInfo(!showInfo);
  };

  return (
    <div className="p-4 max-w-full mx-auto">
      <div className="flex items-center justify-center mb-6 relative">
        <h1 className="text-3xl font-bold text-center">BLADE</h1>
        <button onClick={toggleInfo} className="ml-2 focus:outline-none">
          <Info className="w-5 h-5 text-blue-500" />
        </button>
        {showInfo && (
          <div
            ref={infoRef}
            className="absolute top-full mt-2 p-6 bg-white border border-gray-200 rounded-lg shadow-xl z-10 max-w-2xl text-sm text-gray-700 leading-relaxed"
          >
            <h2 className="text-lg font-semibold mb-3 text-gray-900">
              About BLADE (Bad Language Detection) by Trans Media Sosial
            </h2>

            <p className="mb-3">
              BLADE is an advanced sentiment analysis dashboard specifically
              designed to detect and monitor negative sentiments in Indonesian
              language content across various digital platforms. It employs a
              sophisticated lexicon-based approach tailored for Bahasa
              Indonesia, focusing on identifying and analyzing negative
              expressions, criticisms, and potentially harmful content.
            </p>

            <h3 className="font-semibold mt-4 mb-2 text-gray-800">
              How It Works:
            </h3>
            <ol className="list-decimal list-inside mb-3 space-y-2">
              <li>
                <span className="font-medium">Lexicon-Based Analysis:</span>{" "}
                Utilizes a curated dictionary of Indonesian words and phrases
                associated with negative sentiments. Each term is assigned a
                sentiment score based on its intensity.
              </li>
              <li>
                <span className="font-medium">Contextual Understanding:</span>{" "}
                Considers the context of words, accounting for negations,
                intensifiers, and common linguistic patterns in Bahasa
                Indonesia.
              </li>
              <li>
                <span className="font-medium">Multi-Platform Monitoring:</span>{" "}
                Analyzes content from various sources including news articles,
                Twitter posts, Instagram content, and Facebook discussions.
              </li>
              <li>
                <span className="font-medium">Real-Time Updates:</span>{" "}
                Continuously fetches and processes new data to provide
                up-to-date insights on sentiment trends.
              </li>
            </ol>

            <h3 className="font-semibold mt-4 mb-2 text-gray-800">
              Key Features:
            </h3>
            <ul className="list-disc list-inside mb-3 space-y-2">
              <li>
                <span className="font-medium">Negative Sentiment Focus:</span>{" "}
                Specifically designed to identify and highlight negative
                sentiments, allowing for quick identification of potential
                issues or concerns.
              </li>
              <li>
                <span className="font-medium">Language Specificity:</span>{" "}
                Tailored for the nuances of Bahasa Indonesia, including common
                expressions, slang, and regional variations.
              </li>
              <li>
                <span className="font-medium">Customizable Searches:</span>{" "}
                Allows users to input and track specific queries or topics of
                interest.
              </li>
              <li>
                <span className="font-medium">Cross-Platform Analysis:</span>{" "}
                Provides a comprehensive view of sentiment across different
                social media and news platforms.
              </li>
            </ul>

            <p className="mt-4">
              <span className="font-semibold">Limitations:</span> While BLADE is
              highly effective, it's important to note that automated sentiment
              analysis may not capture all nuances of human communication, such
              as sarcasm or context-dependent expressions. The system is
              continuously being refined to improve accuracy and reduce false
              positives.
            </p>

            <p className="mt-3">
              BLADE serves as a powerful tool for monitoring public opinion,
              managing brand reputation, and identifying emerging issues in the
              Indonesian digital landscape. It's particularly useful for
              organizations, researchers, and analysts interested in
              understanding sentiment trends and potential areas of concern in
              online discussions.
            </p>
          </div>
        )}
      </div>
      <form onSubmit={handleSearch} className="mb-6 flex gap-2 justify-center">
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter search query"
          className="max-w-md"
        />
        <Button type="submit" disabled={loading}>
          {loading ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            "Search"
          )}
        </Button>
      </form>

      <div className="mb-4">
        <h2 className="text-lg font-semibold mb-2">Active Searches:</h2>
        <div className="flex flex-wrap gap-2 mb-4">
          {activeSearches.map((search) => (
            <div
              key={search}
              className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full flex items-center"
            >
              <span className="mr-1">{search}</span>
              <button
                onClick={() => removeSearch(search)}
                className="text-blue-600 hover:text-blue-800"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {archivedSearches.length > 0 && (
        <div className="mb-4">
          <h2 className="text-lg font-semibold mb-2">Archived Searches:</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {archivedSearches.map((search) => (
              <div
                key={search}
                className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full flex items-center"
              >
                <span className="mr-1">{search}</span>
                <button
                  onClick={() => recallSearch(search)}
                  className="text-gray-600 hover:text-gray-800 mr-1"
                >
                  <Plus size={14} />
                </button>
                <button
                  onClick={() => deleteArchivedSearch(search)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded flex items-center justify-center">
          <AlertCircle className="mr-2" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {SOURCES.map((source) => (
          <div key={source} className="flex flex-col">
            <h2 className="text-xl font-semibold mb-4 capitalize">{source}</h2>
            {loading &&
            Object.values(results).every((r) => r[source]?.length === 0) ? (
              <div className="flex justify-center items-center h-32">
                <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="space-y-4 overflow-auto max-h-[calc(100vh-350px)]">
                {activeSearches.map(
                  (searchQuery) =>
                    !results[searchQuery]?.[source]?.length && (
                      <p
                        key={searchQuery}
                        className="text-gray-500 text-sm mb-3"
                      >
                        No results for "{searchQuery}" in {source}.
                      </p>
                    )
                )}
                {Object.entries(results)
                  .flatMap(([searchQuery, sourceResults]) =>
                    (sourceResults[source] || []).map((item) => ({
                      ...item,
                      searchQuery,
                      published_at_iso: new Date(
                        item.published_at
                      ).toISOString(),
                      adjusted_published_at: new Date(
                        item.published_at
                      ).toLocaleString("en-US", {
                        timeZone: "Asia/Jakarta",
                        hour12: false,
                        year: "numeric",
                        month: "2-digit",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit",
                        second: "2-digit",
                      }),
                    }))
                  )
                  .sort(
                    (a, b) =>
                      new Date(b.published_at_iso) -
                      new Date(a.published_at_iso)
                  )
                  .map((item) => (
                    <Card
                      key={`${item.searchQuery}-${item.url}`}
                      className={`hover:shadow-lg transition-all duration-300 mb-3 relative ${
                        newItems[item.searchQuery]?.[source]?.some(
                          (newItem) => newItem.url === item.url
                        )
                          ? "animate-pulse bg-yellow-50"
                          : ""
                      }`}
                    >
                      <div
                        className={`absolute top-0 right-0 ${getColorForQuery(
                          item.searchQuery
                        )} text-white text-xs px-2 py-1 rounded-bl-md rounded-tr-md`}
                      >
                        {item.searchQuery}
                      </div>
                      <CardContent className="p-3 pt-6">
                        <p className="text-xs font-bold">{item.author}</p>
                        <p className="text-xs text-gray-500">
                          {item.adjusted_published_at} WIB
                        </p>
                        <p className="text-xs my-1">
                          {truncateText(item.description || item.title, 3)}
                        </p>
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-500 hover:underline text-xs"
                        >
                          Read more
                        </a>
                      </CardContent>
                    </Card>
                  ))}
                {activeSearches.length === 0 && (
                  <div className="text-center text-gray-500 py-8">
                    No active searches. Add a search query to see results.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="text-center text-white text-xs pt-6">
        Bad Language Detection by TMS @2024
      </div>
    </div>
  );
};

export default SentimentDashboard;
